import React, {
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useRef,
} from "react";
import io, { Socket } from "socket.io-client";
import ChatBot, { Message } from "./ChatBot";
import { ProfileIcon } from "../../../assets/Icons/ProfileIcon";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Paper,
  Dialog,
} from "@mui/material";
import ProgressBar from "./ProgressBar";
import DOMPurify from "dompurify";
import SendButtonIcon from "../../../assets/Icons/SendButtonIcon";
import { MessageProfileIcon } from "../../../assets/Icons/MessageProfileIcon";
import ProfileIconWithInitials from "../../../assets/Icons/ProfileIconWithInitials";
import UserIconSVG from "../../../assets/Icons/UserIconSVG";
import { eximiusChatUrl } from "../../../api/agent";
import { Call } from "@vapi-ai/web/dist/api";
import Vapi from "@vapi-ai/web";

const apiKey = process.env.REACT_APP_VAPI_KEY ?? "";
const vapi = new Vapi(apiKey);

interface RecruiterBotProps {
  onClose: () => void; // Add the onClose prop
  urlCode: any;
}

interface ServerMessage {
  message: string;
  sender: "system" | "bot" | "user" | "assistant";
  timestamp?: string;
  user_name?: string;
  choices?: string[];
  choice_type?: string;
  url?: string;
  stage_list?: string[];
  animation: "";
  current_stage?: string | null;
  chat_history?: ChatHistoryEntry[];
  session_completed?: boolean;
}

interface ChatHistoryEntry {
  stage: string;
  started_at: string;
  ended_at: string;
  conversation: {
    timestamp: string;
    role: "system" | "bot" | "user" | "assistant";
    message: string;
    choices?: string[];
    choicetype?: string;
    choicerating?: number[] | null;
    url?: string;
    type: string;
  }[];
}

interface StyleProps {
  headerBgColor?: string;
  headerTextColor?: string;
  chatBgColor?: string;
  botMessageBgColor?: string;
  userMessageBgColor?: string;
  botTextColor?: string;
  userTextColor?: string;
  fontFamily?: string;
  fontSize?: {
    header?: string;
    message?: string;
    timestamp?: string;
    subheader?: string;
  };
  profileIcon?: React.ComponentType<any>;
}

const defaultFontSize = {
  header: "1.25rem",
  message: "14px",
  timestamp: "0.7rem",
  subheader: "16px",
};

const styleProps: Required<StyleProps> = {
  headerBgColor: "#FFFFFF",
  headerTextColor: "#414141",
  chatBgColor: "white",
  botMessageBgColor: "white",
  userMessageBgColor: "white",
  botTextColor: "#343942",
  userTextColor: "#343942",
  fontFamily: "Lato, sans-serif",
  fontSize: defaultFontSize,
  profileIcon: ProfileIcon,
};

export const isValidServerMessage = (data: any): data is ServerMessage => {
  return (
    typeof data === "object" &&
    data !== null &&
    (typeof data.message === "string" ||
      Array.isArray(data.chat_history) ||
      Array.isArray(data.stage_list) ||
      typeof data.current_stage === "string")
  );
};

export const formatOldTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const month = date.toLocaleString("default", { month: "short" });
  const day = date.getDate();
  const time = date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${month} ${day}, ${time}`;
};

const getCurrentTime = (): string => {
  return new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

type ExtractResult = {
  remainingMessage: string;
};

export const extractJsonAndMessage = (inputMessage: string): ExtractResult => {
  // Regular expressions to match JSON content in two formats:
  // 1. Enclosed in ```json ... ```
  const jsonPatternBlock = /```json\s*([\s\S]*?)\s*```/;
  // 2. Inline JSON - matches complete JSON objects including nested structures
  const jsonPatternInline = /{(?:[^{}]|{(?:[^{}]|{[^{}]*})*})*}/g;
  // 3. Match for Conversation Tracker marker
  const conversationTrackerMarker = /\*\*`Conversation Tracker`\*\*/;

  // Check for JSON enclosed in ```json ... ```
  let match = inputMessage.match(jsonPatternBlock);
  let jsonContent = "";

  // If no match in code blocks, check for inline JSON
  if (!match) {
    match = inputMessage.match(jsonPatternInline);
    if (match) {
      // For inline JSON, take the entire matched content
      jsonContent = match[0];
    }
  } else {
    // For code block JSON, extract content between the backticks
    jsonContent = match[1];
  }

  if (jsonContent) {
    try {
      // Clean the JSON string before parsing
      // const cleanJsonStr = jsonContent.replace(/```json|```/g, "").trim();
      // const extractedJson = JSON.parse(cleanJsonStr);

      // Find start positions of various markers
      const jsonBlockStart = inputMessage.indexOf("```json");
      const conversationTrackerStart = inputMessage.search(
        conversationTrackerMarker
      );

      // Determine the actual cut-off point
      let cutoffIndex;
      if (conversationTrackerStart !== -1) {
        // If Conversation Tracker marker exists, use that as the cutoff
        cutoffIndex = conversationTrackerStart;
      } else if (jsonBlockStart !== -1) {
        // If no Conversation Tracker but ```json exists, use that
        cutoffIndex = jsonBlockStart;
      } else {
        // Otherwise use the start of the JSON content itself
        cutoffIndex = inputMessage.indexOf(jsonContent);
      }

      // Extract the text before the determined cutoff point
      const remainingMessage = inputMessage.substring(0, cutoffIndex).trim();

      return { remainingMessage};
    } catch (error) {
      console.error(`Error parsing JSON: ${error}`);
      return { remainingMessage: inputMessage};
    }
  }

  return { remainingMessage: inputMessage};
};

const convertToClientMessage = (
  serverMessage: ServerMessage,
  isOldMessage: boolean = false
): Message => {
  const { remainingMessage } = extractJsonAndMessage(
    serverMessage.message || ""
  );

  return {
    text: remainingMessage || "",
    sender: serverMessage.sender || "system",
    time:
      isOldMessage && serverMessage.timestamp
        ? formatOldTimestamp(serverMessage.timestamp)
        : getCurrentTime(),
    choices: serverMessage.choices,
    choiceType: serverMessage.choice_type,
    url: serverMessage.url,
  };
};

const RecruiterBot: React.FC<RecruiterBotProps> = ({ onClose, urlCode }) => {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputText, setInputText] = useState("");
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [hasError, setHasError] = useState<boolean>(false);
  const [isSessionCompleted, setIsSessionCompleted] = useState<boolean>(false);
  const [isRecruiterbot, setIsRecruiterbot] = useState(true);
  const [stageList, setStageList] = useState<string[]>([]);
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [isTextboxEnabled, setIsTextboxEnabled] = useState(true);
  const [previousBotMessage, setPreviousBotMessage] = useState<string | null>(
    null
  );
  const myVariableRef = useRef(null);
  const [myVariable] = useState(null);
  const [congratulation, setCongratulation] = useState(false);

  const url_code = urlCode;

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    myVariableRef.current = myVariable; // Update ref with the current state value
  }, [myVariable]);

  const handleModalClose = () => {
    onClose();
  };



  const handleWelcomeResponse = (data: ServerMessage) => {
    if (!isValidServerMessage(data)) {
      console.error("Invalid welcome-response data:", data);
      return;
    }
  
    if (data.chat_history) {
      handleChatHistory(data.chat_history);
    } else {
      handleNewUser (data);
    }
  
    if (data.user_name) {
      setUserName(data.user_name);
    }
  
    if (data.stage_list) {
      setStageList(data.stage_list);
    }
  
    if (data.current_stage) {
      setCurrentStage(data.current_stage);
    }
  
    // Check if the session is completed
    if (data.session_completed) {
      setIsSessionCompleted(true);
      setIsTextboxEnabled(false);
    } else {
      setIsSessionCompleted(false);
      setIsTextboxEnabled(true);
    }
  };

  const extractMessagesFromArray = (messages: Message[]): Message[] => {
    return messages.map((msg) => {
      const rawMessage = msg.text;
      // Regular expression to match JSON content wrapped in ```json ... ```
      const regex = /```json[\s\S]*?```/g;
      // Match an opening curly brace followed by any characters until a balanced closing brace
      const jsonPatternInline = /{(?:[^{}]|{(?:[^{}]|{[^{}]*})*})*}/g;
      let cleanedMessage = rawMessage;
      // First try to find JSON blocks enclosed in ```json ... ```
      let match = rawMessage.match(regex);
      let isCodeBlock = false;

      // If no match for code blocks, try to find inline JSON
      if (!match) {
        match = rawMessage.match(jsonPatternInline);
      } else {
        isCodeBlock = true;
      }

      if (match) {
        let startIndex;
        if (isCodeBlock) {
          // For code blocks, find the ```json marker
          startIndex = rawMessage.indexOf("```json");
        } else {
          // For inline JSON, find the { marker
          startIndex = rawMessage.indexOf(match[0]);
        }
        // Take only the text before the JSON
        cleanedMessage = rawMessage.substring(0, startIndex).trim();
      }

      msg.text = cleanedMessage;
      return msg;
    });
  };
  const handleChatHistory = (chatHistory: ChatHistoryEntry[]) => {
    const flattenedMessages = chatHistory.flatMap((stage) => {
      return stage.conversation.map((msg) => {
        if (
          msg.type === "conversation" &&
          (msg.role === "system" || msg.role === "user" || msg.role === "bot" || msg.role === "assistant")
        ) {
          updateTextboxState(msg);
          return convertToClientMessage(
            {
              message: msg.message,
              sender: msg.role,
              timestamp: msg.timestamp,
              choices: msg.choices ?? undefined,
              animation: "",
              choice_type: msg.choicetype ?? undefined,
              url: msg.url ?? undefined,
            },
            true
          );
        }
        return undefined; // Explicitly return undefined for non-matching cases
      });
    });
    // Filter out undefined messages
    const validMessages = flattenedMessages.filter(
      (msg): msg is Message => msg !== undefined
    );
    const messages = extractMessagesFromArray(validMessages);
    setMessages(messages); // Pass only valid messages
    setCurrentStage(getLastNonEmptyStage(chatHistory)?.stage ?? null);
  };

  const handleNewUser = (data: ServerMessage) => {
    // setIsConnected(true)
    const newMessage = convertToClientMessage(data);
    setMessages([newMessage]);
  };

  const updateTextboxState = (msg: any) => {
    if (msg.choicetype === "textbox") {
      setIsTextboxEnabled(true);
      setPreviousBotMessage(msg.message);
    } else if (msg.choicetype === "radio") {
      setIsTextboxEnabled(false);
    } else {
      setPreviousBotMessage(null);
    }
  };

  const getLastNonEmptyStage = (chatHistory: ChatHistoryEntry[]) => {
    return chatHistory.filter((stage) => stage.conversation.length > 0).pop();
  };

  const setupSocketListeners = (socket: Socket) => {
    socket.on("connect", () => {
      console.log("Connected to server");
      setIsConnected(true);
      setIsRecruiterbot(true);
      setError(null);
    });

    socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
      setError("Failed to connect to the server. Please try again later.");
      setIsConnected(false);
      setHasError(true);
    });

    socket.on("disconnect", () => {
      setIsConnected(false);
      setIsSessionCompleted(true);
    });

    socket.on("welcome-response", handleWelcomeResponse);
    socket.on("response", handleResponse);
  };

  useEffect(() => {
    // console.log("URL", url_code);
    if (url_code) {
      const newSocket = io(`${eximiusChatUrl}/pg_recruiterbot`, {
        query: {
          url_code: url_code,
        },
        transports: ["websocket"],
        reconnection: true, // Enable reconnection
        reconnectionAttempts: 5, // Limit reconnection attempts
      });

      setSocket(newSocket);
      setupSocketListeners(newSocket);

      return () => {
        if (newSocket) {
          newSocket.disconnect();
        }
      };
    }
  }, [url_code]);

  const handleSend = useCallback(() => {
    if (inputText.trim() && socket) {
      let messageToSend = inputText;
      let messageToEmit = "";
      let field = null;

      if (isTextboxEnabled && previousBotMessage) {
        const fields = [
          "phone number",
          "address",
          "experience",
          "work authorization",
        ];
        field = fields.find((f) =>
          previousBotMessage.toLowerCase().includes(f.toLowerCase())
        );
        if (field) {
          messageToEmit = `${field}: ${messageToSend}`;
        }
      }

      const newMessage: Message = {
        text: messageToSend,
        sender: "user",
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((prev) => [...prev, newMessage]);
      if (field) {
        socket.emit("message", {
          response: messageToEmit,
        });
      } else {
        socket.emit("message", {
          response: messageToSend,
        });
      }

      setInputText("");
    }
  }, [inputText, socket, isTextboxEnabled, previousBotMessage]);

  const handleOptionClick = useCallback(
    (option: string) => {
      if (socket) {
        const newMessage: Message = {
          text: option,
          sender: "user",
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
        };
        setMessages((prev) => [...prev, newMessage]);
        if (messages.length >= 1) {
          socket.emit("message", {
            response: option,
          });
        }
      }
    },
    [socket, currentStage, messages]
  );

  const handleResponse = (data: ServerMessage) => {
    if (isValidServerMessage(data)) {
      const newMessage = convertToClientMessage(data);
      setMessages((prev) => [...prev, newMessage]);
      setPreviousBotMessage(data.message);
      if (data.current_stage) {
        setCurrentStage(data.current_stage);
      }
      if (data.animation) {
        setCongratulation(true);
      }

      if (data.stage_list) {
        setStageList(data.stage_list);
      }

      if (data.choice_type === "radio" || data.choice_type === "file") {
        // setIsRecruiterbot(true)
        setIsTextboxEnabled(false);
      } else {
        setIsTextboxEnabled(true);
      }
      if (data.choice_type === "textbox") {
        // setIsRecruiterbot(true)
        setIsTextboxEnabled(true);
        setPreviousBotMessage(data.message);
      } else {
        setPreviousBotMessage(null);
      }
    } else {
      console.error("Invalid response data:", data);
    }
  };

  const renderConnection = () => {
    if (hasError) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "#FF5722",
            display: "flex",
            alignItems: "center",
            fontSize: "12px",
            lineHeight: 1,
            fontFamily: styleProps.fontFamily,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#FF5722",
              mr: 0.5,
            }}
          />
          Error occurred
        </Typography>
      );
    } else if (isSessionCompleted) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: "#4CAF50",
            display: "flex",
            alignItems: "center",
            fontSize: "12px",
            lineHeight: 1,
            fontFamily: styleProps.fontFamily,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#4CAF50",
              mr: 0.5,
            }}
          />
          Session completed
        </Typography>
      );
    } else {
      return (
        <Typography
          variant="body2"
          sx={{
            color: isConnected ? "#4BEA56" : "#FF5722",
            display: "flex",
            alignItems: "center",
            fontSize: "12px",
            lineHeight: 1,
            fontFamily: styleProps.fontFamily,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: isConnected ? "#4BEA56" : "#FF5722",
              mr: 0.5,
            }}
          />
          {isConnected ? "Connected" : "Connecting"}
        </Typography>
      );
    }
  };

  const renderHeader = () => (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      <ProfileIcon style={{ width: 40, height: 40, marginRight: 8 }} />
      <Box>
        <Typography
          variant="h6"
          sx={{
            fontFamily: styleProps.fontFamily,
            fontSize: styleProps.fontSize.header,
            fontWeight: 700,
          }}
        >
          Recruiter Bot
        </Typography>
        {renderConnection()}
      </Box>
    </Box>
  );

  const renderConnectionStatus = () => {
    if (!isConnected && !hasError && !isSessionCompleted) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
          }}
        >
          <CircularProgress size={24} sx={{ mb: 2 }} />
          <Typography variant="body1" className="recruiter-bot-font">
            Connecting to recruiter bot, please wait...
          </Typography>
        </Box>
      );
    }
    return null;
  };

  const renderMessageContent = (message: Message) => {
    const containsHtml = /<[a-z][\s\S]*>/i.test(message.text);
  
    // Replace multiple newlines with a single newline
    let modifiedText = message.text.replace(/\n+/g, "\n").replaceAll("\n", "<br>"); // Replace newlines first
    const sanitizedContent = DOMPurify.sanitize(modifiedText);
  
    return (
      <Box
        sx={{
          p: 1,
          borderColor: "#D4DDEB",
        }}
      >
        <Typography
          variant="subtitle2"
          color="#1c2a61"
          className="recruiter-bot-message-source-heading"
          sx={{
            textAlign: message.sender === "user" ? "right" : "left",
          }}
        >
          {message.sender === "system" || message.sender === "bot" || message.sender === "assistant"
            ? "Recruiter"
            : "Candidate"}
        </Typography>
        <Box
          className="recruiter-bot-font recruiter-bot-message"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "flex-end",
            width: "100%",
          }}
        >
          {containsHtml ? (
            <div
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
              style={{
                flex: "1 1 100%", // Ensures it takes full width before wrapping
                marginRight: "8px",
                whiteSpace: "normal",
                wordBreak: "break-word",
                color:
                  message.sender === "bot" || message.sender === "system" || message.sender === "assistant"
                    ? styleProps.botTextColor
                    : styleProps.userTextColor,
              }}
            />
          ) : (
            <Typography
              variant="body2"
              color={
                message.sender === "bot" || message.sender === "system" || message.sender === "assistant"
                  ? styleProps.botTextColor
                  : styleProps.userTextColor
              }
              className="recruiter-bot-font recruiter-bot-message"
              sx={{
                flex: "1 1 100%", // Ensures it takes full width before wrapping
                mr: 1,
                whiteSpace: "normal",
                textAlign: message.sender === "user" ? "right" : "left",
                wordBreak: "break-word",
              }}
              dangerouslySetInnerHTML={{ __html: modifiedText }}
            ></Typography>
          )}
  
          <Typography
            variant="caption"
            sx={{
              color: "#7E848F",
              fontSize: styleProps.fontSize.timestamp,
              fontFamily: styleProps.fontFamily,
              whiteSpace: "nowrap",
              flexShrink: 0,
              marginLeft: "auto", // Moves it to the end of the row
            }}
          >
            {message.time}
          </Typography>
        </Box>
      </Box>
    );
  };
  const renderMessage = (message: Message, index: number) => {
    const messageKey = `${index}-${message.sender}`;
    let profileIcon;
    if (message.sender === "system" || message.sender === "bot" || message.sender === "assistant") {
      profileIcon = (
        <MessageProfileIcon
          style={{
            width: 40,
            height: 40,
            marginRight: 8,
            flexShrink: 0,
          }}
        />
      );
    } else if (userName === "") {
      profileIcon = (
        <UserIconSVG
          style={{
            marginLeft: "7px", // Adjust this to add more space between the icon and the message
            marginRight: "0px",
            flexShrink: 0,
          }}
        />
      );
    } else {
      profileIcon = (
        <ProfileIconWithInitials
          name={userName}
          size={40}
          style={{
            marginLeft: 8,
            flexShrink: 0,
          }}
        />
      );
    }

    return (
      <Box
        key={messageKey}
        sx={{
          mb: 1.5,
          display: "flex",
          flexDirection:
            message.sender === "bot" || message.sender === "system"
              ? "row"
              : "row-reverse",
          alignItems: "flex-start",
        }}
      >
        {profileIcon}
        <Box
          sx={{ flex: 1, maxWidth: message.sender === "user" ? "69%" : "70%" }}
        >
          <Paper
            className="recruiter-bot-message-no-border"
            elevation={0}
            sx={{
              bgcolor:
                message.sender === "bot" || message.sender === "system" || message.sender === "assistant"
                  ? "#F3F3FF"
                  : "#F3F3F3",
              border: 1,
              borderColor: "#D4DDEB",
              borderRadius: 2,
              overflow: "hidden",
              ml: message.sender === "user" ? "auto" : 0,
            }}
          >
            {renderMessageContent(message)}
          </Paper>
        </Box>
      </Box>
    );
  };

  const renderStatusMessages = () => (
    <>
      {hasError && (
        <Typography
          variant="body1"
          sx={{ color: "#FF5722", textAlign: "center", mt: 1 }}
        >
          An error occurred. Please try again later.
        </Typography>
      )}
      {isSessionCompleted && (
        <Typography
          variant="body1"
          sx={{ color: "#4CAF50", textAlign: "center", mt: 1 }}
        >
          Your progress has been saved
        </Typography>
      )}
      {isSessionCompleted && (
        <Typography
          variant="body1"
          sx={{ color: "#4CAF50", textAlign: "center", mt: 1 }}
        >
          Session completed. Thank you for your time!
        </Typography>
      )}
    </>
  );

  const renderMessages = (messages: Message[]) => (
    <>
      {renderConnectionStatus()}
      {isConnected || hasError || isSessionCompleted ? (
        <>
          {messages
            .filter(
              (n) =>
                n?.sender !== null &&
                ["bot", "user", "system", "assistant"].includes(n.sender.toLowerCase())
            )
            .map(renderMessage)}
          {renderStatusMessages()}
          {/* This div will act as the scroll target */}
          <div ref={messagesEndRef} />
        </>
      ) : null}
    </>
  );

  const renderInputArea = (): ReactNode => {
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{ 
            display: "flex", 
            alignItems: "center", 
            marginBottom: "5px",
            maxHeight: "80px" // Limiting the overall height of the input area
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end", // Changed from center to flex-end
              bgcolor: "#F4F4F4",
              borderRadius: "20px",
              border: "1px solid #E5E5E5",
              p: 1,
              flexGrow: 1,
              mr: 2,
              minHeight: "40px", // Add minimum height
              maxHeight: "65px", // Reduced from 120px to 70px
              overflowY: "visible" // Changed from auto to visible
            }}
          >
            <TextField
              className="chat-input recruiter-bot-input"
              fullWidth
              multiline
              maxRows={3} // Reduced from 4 to 3 rows maximum
              variant="standard"
              placeholder="Type a message"
              data-testid="text-box"
              value={inputText}
              onChange={(e: any) => setInputText(e.target.value)}
              onKeyPress={(e: any) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault(); // Prevent default to avoid newline
                  handleSend();
                }
              }}
              InputProps={{
                disableUnderline: true,
              }}
              sx={{ 
                flexGrow: 1,
                "& .MuiInputBase-root": {
                  alignItems: "flex-end", // Align text at the bottom
                  maxHeight: "70px", // Reduced maximum height for the input component
                  overflowY: "auto", // Enable scrolling within the input field
                },
                "& .MuiInputBase-inputMultiline": {
                  boxSizing: "border-box", // Ensures padding is included in element size
                  overflowY: "auto" // Enable scrolling for the input field itself
                }
              }}
              inputProps={{
                style: {
                  paddingTop: '4px',
                },
              }}
              slotProps={{
                formHelperText: {
                  style: { marginTop: '-4px' } 
                },
                inputLabel: {
                  shrink: true,
                  style: { marginTop: '-8px' } 
                }
              }}
              disabled={
                !isConnected ||
                hasError ||
                isSessionCompleted ||
                (!isTextboxEnabled && isRecruiterbot)
              }
            />
          </Box>
          <IconButton
            onClick={handleSend}
            disabled={
              !isConnected ||
              hasError ||
              isSessionCompleted ||
              (!isTextboxEnabled && isRecruiterbot)
            }
            sx={{
              bgcolor: "#1c2a61",
              color: "white",
              "&:hover": { bgcolor: "#0051cc" },
              "&.Mui-disabled": { bgcolor: "#1c2a61", color: "#ffffff80" },
              width: 44,
              height: 44,
              flexShrink: 0,
            }}
          >
            <SendButtonIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const progressBar = (
    <ProgressBar stages={stageList} currentStage={currentStage} />
  );

  const chatBotProps = {
    messages,
    inputText,
    setInputText,
    handleSend,
    handleOptionClick,
    isConnected,
    styleProps,
    renderHeader,
    // renderMessages: isProgressSaved ? renderProgressSaved : renderMessages,
    renderMessages: renderMessages,
    renderInputArea,
    disableInput: !isConnected || hasError || isSessionCompleted,
    progressBar,
    setCongratulation,
    onClose,
  };

  if (error) {
    return (
      <div className="error-message">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
  <Dialog open onClose={handleModalClose} maxWidth="xs" fullWidth> 
    <div className="recruiter-bot-container manish">
      <ChatBot
        {...chatBotProps}
        congratulation={congratulation}
        onClose={handleModalClose}
      />
    </div>
  </Dialog>
  );
};

export default RecruiterBot;
